import { type Moment } from 'moment'
import {
  SingleDatePicker as ReactSingleDatePicker,
  type SingleDatePickerShape,
} from 'react-dates'
import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'

import { IconCalendar } from '../Icon'
import { DatepickerCalendarPresets } from './components/DatepickerCalendarPresets'
import { DatepickerNextNavButton } from './components/DatepickerNextNavButton'
import { DatepickerPrevNavButton } from './components/DatepickerPrevNavButton'
import { DeprecatedDatePickerStyled } from './styles/DatepickerStyled'
import { getAnalyticsDataAttrs } from '@ui/utils/analytics'

const PrevNavButton = <DatepickerPrevNavButton />
const NextNavButton = <DatepickerNextNavButton />
const CustomInputIcon = (
  <IconCalendar color='textPrimary' ariaLabel='Open calendar' />
)

/** @deprecated Use the new DatePicker component instead */
export interface DeprecatedSingleDatePickerPreset {
  label: string
  date: Moment
  [x: string]: any
}

/** @deprecated Use the new DatePicker component instead */
export interface IDeprecatedSingleDatePickerProps extends Omit<
  SingleDatePickerShape,
  'onDateChange' | 'focused' | 'onFocusChange' | 'date'
> {
  /**
   * Unique identifier for analytics tracking of date picker interactions.
   */
  analyticsId?: string
  error?: boolean
  presets?: DeprecatedSingleDatePickerPreset[]
  presetTitle?: string
  date?: SingleDatePickerShape['date']
  onDateChange?: (date: moment.Moment, source?: 'calendar' | 'preset') => void
  focused?: SingleDatePickerShape['focused']
  onFocusChange?: SingleDatePickerShape['onFocusChange']
  onPresetChange?: (preset: DeprecatedSingleDatePickerPreset) => void
}

/** @deprecated Use the new DatePicker component instead */
export const DeprecatedSingleDatePicker = ({
  analyticsId,
  error,
  presets,
  presetTitle,
  onPresetChange = () => undefined,
  date = null,
  onDateChange = () => undefined,
  focused = false,
  onFocusChange = () => undefined,
  ...otherProps
}: IDeprecatedSingleDatePickerProps) => {
  const onPresetClick = (preset: DeprecatedSingleDatePickerPreset) => {
    onDateChange(preset.date, 'preset')
    onPresetChange(preset)
  }

  const _onDateChange = (newDate) => {
    onDateChange(newDate, 'calendar')
  }

  const analyticsProps = getAnalyticsDataAttrs(analyticsId)

  return (
    <DeprecatedDatePickerStyled error={error} {...analyticsProps}>
      <ReactSingleDatePicker
        date={date}
        focused={focused}
        onFocusChange={onFocusChange}
        navPrev={PrevNavButton}
        navNext={NextNavButton}
        customInputIcon={CustomInputIcon}
        showDefaultInputIcon={true}
        inputIconPosition='after'
        daySize={34}
        verticalHeight={500}
        verticalSpacing={0}
        calendarInfoPosition='after'
        hideKeyboardShortcutsPanel
        renderCalendarInfo={
          presets
            ? () => (
                <DatepickerCalendarPresets
                  presets={presets}
                  title={presetTitle}
                  onClick={(preset) => onPresetClick(preset)}
                />
              )
            : null
        }
        numberOfMonths={1}
        onDateChange={_onDateChange}
        {...otherProps}
      />
    </DeprecatedDatePickerStyled>
  )
}

DeprecatedSingleDatePicker.displayName = 'DeprecatedSingleDatePicker'
